import React from 'react'
import {
    Breakpoints,
    CTA,
    Text,
    TextAlignment,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import {ChevronRight, Pin} from '@volkswagen-onehub/icons-core'
import {useTranslation} from 'react-i18next'
import {JobAdvertisement} from '../types/jobAdvertisement'
import {Category} from '../types/category'

const JobAdvertisement = styled.div`
    background: ${(props) => props.theme.colors.background.card.primary};
    border: none;
    border-radius: ${(props) => props.theme.size.borderRadius.card};
    box-shadow: ${(props) => props.theme.colors.shadow.small};
    cursor: pointer;
    padding: ${(props) => props.theme.size.static350};
    transition: box-shadow ${(props) => props.theme.animation.duration.layer}
        ${(props) => props.theme.animation.timingFunction.cubicOut};

    @media (min-width: ${Breakpoints.b960}px) {
        padding: ${(props) => props.theme.size.static450};
    }

    &:not(:last-child) {
        margin-bottom: ${(props) => props.theme.size.static200};
    }

    &:hover {
        box-shadow: ${(props) => props.theme.colors.shadow.smallHover};
    }
`

const JobAdvertisementInner = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    gap: ${(props) => props.theme.size.static200};
    flex-wrap: wrap;

    @media (min-width: ${Breakpoints.b560}px) {
        flex-wrap: nowrap;
    }

    @media (min-width: ${Breakpoints.b960}px) {
        justify-content: space-between;
        gap: ${(props) => props.theme.size.static400};
    }
`

const Date = styled(Text as any)`
    margin-bottom: ${(props) => props.theme.size.static150};
`

const JobAdvertisementBottom = styled.div`
    display: flex;
    gap: ${(props) => props.theme.size.static400};
    margin-top: ${(props) => props.theme.size.static350};
`

const JobAdvertisementInfo = styled.div`
    align-items: center;
    display: flex;
    gap: ${(props) => props.theme.size.static200};
`

const JobAdvertisementCta = styled(CTA as any)`
    height: auto;
    width: auto;
`

const ChevronRightStyled = styled(ChevronRight as any)`
    height: ${(props) => props.theme.size.static515};
    width: auto;
`

const RemoveFiltersBottom = styled(Text as any)`
    margin-top: ${(props) => props.theme.size.static450};
`

const onOpenPositionClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const parent =
        (e.target as HTMLElement).closest('.job-advertisement') ?? e.target

    window.open((parent as HTMLElement)?.querySelector('a')?.href)
}

interface JobAdvertisementsListProps {
    categories: Category[]
    jobAdvertisementsDisplaying: JobAdvertisement[]
    myDealerCode: string | undefined
    removeFilters: () => void
    layout: string
}

export const JobAdvertisementsList: React.FunctionComponent<
    JobAdvertisementsListProps
> = (props) => {
    const {categories, jobAdvertisementsDisplaying, removeFilters} = props
    const {t} = useTranslation('fa-posizioni-aperte')

    return (
        <div>
            {jobAdvertisementsDisplaying.length ? (
                jobAdvertisementsDisplaying
                    .sort(
                        (jobAdvertisement1, jobAdvertisement2) =>
                            new window.Date(
                                jobAdvertisement1.publication_date
                            ).getTime() +
                            new window.Date(
                                jobAdvertisement2.publication_date
                            ).getTime()
                    )
                    .map((jobAdvertisement, index) => {
                        let categoryName = ''

                        categories.some((category) =>
                            category.categories.some(({id}) => {
                                if (id === jobAdvertisement.category_id) {
                                    categoryName = category.name

                                    return true
                                }

                                return false
                            })
                        )

                        return (
                            <JobAdvertisement
                                className="job-advertisement"
                                key={index}
                                onClick={(e) => onOpenPositionClick(e)}
                            >
                                <JobAdvertisementInner>
                                    <div>
                                        <Date
                                            appearance={
                                                TokenTextAppearance.label150
                                            }
                                            bold
                                            color={TokenTextColor.secondary}
                                        >
                                            {t(
                                                'jobAdvertisementsList.publication_date',
                                                {
                                                    publication_date:
                                                        new window.Date(
                                                            jobAdvertisement.publication_date
                                                        ).toLocaleDateString(),
                                                }
                                            )}{' '}
                                            -{' '}
                                            {t(
                                                'jobAdvertisementsList.expiration_date',
                                                {
                                                    expiration_date:
                                                        new window.Date(
                                                            jobAdvertisement.expiration_date
                                                        ).toLocaleDateString(),
                                                }
                                            )}
                                        </Date>

                                        <Text
                                            appearance={
                                                TokenTextAppearance.headline300
                                            }
                                            bold
                                        >
                                            {categoryName}
                                        </Text>

                                        <Text
                                            appearance={
                                                TokenTextAppearance.bigcopy100
                                            }
                                        >
                                            {jobAdvertisement.title}
                                            {props.layout === 'standalone' &&
                                            !props.myDealerCode
                                                ? t(
                                                      'jobAdvertisementsList.location',
                                                      {
                                                          location:
                                                              jobAdvertisement
                                                                  .dealer
                                                                  .company_name,
                                                      }
                                                  )
                                                : undefined}
                                        </Text>

                                        <JobAdvertisementBottom
                                            color={TokenTextColor.secondary}
                                        >
                                            <JobAdvertisementInfo>
                                                <Pin aria-hidden />{' '}
                                                <Text
                                                    appearance={
                                                        TokenTextAppearance.label200
                                                    }
                                                    bold
                                                    color={
                                                        TokenTextColor.secondary
                                                    }
                                                >
                                                    {`${jobAdvertisement.location} (${jobAdvertisement.province})`}
                                                </Text>
                                            </JobAdvertisementInfo>
                                        </JobAdvertisementBottom>
                                    </div>

                                    <JobAdvertisementCta
                                        ariaLabel={jobAdvertisement.title}
                                        emphasis="tertiary"
                                        href={jobAdvertisement.job_link}
                                        icon={
                                            <ChevronRightStyled aria-hidden />
                                        }
                                        tag="a"
                                        target="_blank"
                                    />
                                </JobAdvertisementInner>
                            </JobAdvertisement>
                        )
                    })
            ) : (
                <>
                    {!props.myDealerCode ? (
                        <>
                            <Text
                                appearance={TokenTextAppearance.headline300}
                                textAlign={TextAlignment.center}
                            >
                                {t('jobAdvertisementsList.noResults')}
                            </Text>

                            <RemoveFiltersBottom
                                textAlign={TextAlignment.center}
                            >
                                <CTA
                                    aria-label={t(
                                        'jobAdvertisementsList.removeFilters'
                                    )}
                                    emphasis="secondary"
                                    onClick={(e) => {
                                        e.preventDefault()

                                        removeFilters()
                                    }}
                                    tag="button"
                                >
                                    {t('jobAdvertisementsList.removeFilters')}
                                </CTA>
                            </RemoveFiltersBottom>
                        </>
                    ) : undefined}
                </>
            )}
        </div>
    )
}
